import type { CustomizerReducerActiveModeType } from '@europrocurement/l2d-domain';
import type { Theme } from '@mui/system';

import _ from 'lodash';
import {
    BLUE_THEME,
    GREEN_THEME,
    RED_THEME,
    BLACK_THEME,
    PURPLE_THEME,
    INDIGO_THEME,
    ORANGE_THEME,
    L2D_THEME,
    THEME_LIST,
} from '@europrocurement/l2d-domain';

import { createTheme, lighten } from '@mui/material/styles';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import { useSelector } from 'react-redux';
import { FlexyLayoutState } from '../../redux/storeConfig/store';
import typography from './Typography';
import components from './Override';
import shadows from './Shadows';

declare module '@mui/material/styles/createPalette' {
    interface TypeBackground {
        default: string;
        dark: string;
        paper: string;
    }

    interface TypeText {
        light: string;
    }

    interface PaletteOptions {
        danger?: PaletteColor;
    }
}

declare module '@mui/material/Chip' {
    interface ChipPropsColorOverrides {
        danger: true;
    }
}

// ##############################
// Global Variables
// ##############################
export type ColorsPaletteVariant =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'error'
    | 'danger'
    | 'warning';

export type NamedTheme = ThemeOptions & { name: THEME_LIST };

const SidebarWidth = 260;
const SidebarFoldedWidth = 64;
const TopbarHeight = 72;
const paddingContent = 30;
const footerHeight = 40;
const breadcrumbHeight = 70;

const baseTheme: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: '#1a97f5',
            light: '#e6f4ff',
            dark: '#1682d4',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#1e4db7',
            light: '#ddebff',
            dark: '#173f98',
            contrastText: '#ffffff',
        },
        success: {
            main: '#00c292',
            light: '#ebfaf2',
            dark: '#00964b',
            contrastText: '#ffffff',
        },
        info: {
            main: '#0bb2fb',
            light: '#a7e3f4',
        },
        error: {
            main: '#e46a76',
            light: '#fdf3f5',
            dark: '#e45a68',
        },
        danger: {
            main: '#FF8552',
            contrastText: '#ffffff',
            light: '#ffb680',
            dark: '#c75626',
        },
        warning: {
            main: '#fec90f',
            light: '#fff4e5',
            dark: '#dcb014',
            contrastText: '#ffffff',
        },
        grey: {
            A100: '#ecf0f2',
            A200: '#99abb4',
            A400: '#767e89',
            A700: '#e6f4ff',
        },
        action: {
            disabledBackground: 'rgba(73,82,88,0.12)',
            hoverOpacity: 0.02,
            hover: 'rgba(0, 0, 0, 0.03)',
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 620,
            md: 900,
            lg: 1150,
            xl: 1536,
        },
    },
    shape: {
        borderRadius: 12,
    },
    mixins: {
        toolbar: {
            color: '#949db2',
            '@media(min-width:1280px)': {
                minHeight: TopbarHeight,
                padding: '0 30px',
            },
            '@media(max-width:1280px)': {
                minHeight: '64px',
            },
        },
    },
    components,
    typography,
    shadows,
};

const themesOptions: NamedTheme[] = [
    {
        ...baseTheme,
        name: L2D_THEME,
        palette: {
            primary: {
                main: '#0CBFF2',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#011B58',
                contrastText: '#ffffff',
            },
            success: {
                main: '#62C370',
                contrastText: '#ffffff',
            },
            info: {
                main: '#a7e3f4',
                contrastText: '#ffffff',
            },
            error: {
                main: '#F43939',
                contrastText: '#ffffff',
                100: '#FFDFD4',
                900: '#FF0000',
            },
            danger: {
                main: '#FF8552',
                contrastText: '#ffffff',
                light: '#ffb680',
                dark: '#c75626',
            },
            warning: {
                main: '#F1D302',
                contrastText: '#ffffff',
            },
        },
    },
    {
        ...baseTheme,
        name: BLUE_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#1a97f5',
                light: '#e6f4ff',
                dark: '#1682d4',
            },
            secondary: {
                main: '#1e4db7',
                light: '#ddebff',
                dark: '#173f98',
            },
        },
    },
    {
        ...baseTheme,
        name: GREEN_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#00cec3',
                light: '#d7f8f6',
                dark: '#02b3a9',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#066a73',
            },
        },
    },
    {
        ...baseTheme,
        name: PURPLE_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#7352ff',
                light: '#e5e0fa',
                dark: '#5739d6',
            },
            secondary: {
                main: '#402e8d',
            },
        },
    },
    {
        ...baseTheme,
        name: INDIGO_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#1e4db7',
                light: '#e6f4ff',
                dark: '#0c399e',
            },
            secondary: {
                main: '#11397b',
            },
        },
    },
    {
        ...baseTheme,
        name: ORANGE_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#03c9d7',
                light: '#e5fafb',
                dark: '#05b2bd',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#fb9678',
                light: '#fcf1ed',
                dark: '#e67e5f',
                contrastText: '#ffffff',
            },
        },
    },
    {
        ...baseTheme,
        name: RED_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#ff5c8e',
                light: '#fce6ed',
                dark: '#d43653',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#8e3774',
                light: '#d097bf',
                dark: '#ac516c',
            },
        },
    },
    {
        ...baseTheme,
        name: BLACK_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#1c2025',
            },
        },
    },
];

const themesDarkOveride: NamedTheme[] = [
    {
        ...baseTheme,
        name: L2D_THEME,
        palette: {
            primary: {
                main: '#011B58',
                contrastText: '#e6e5e8',
            },
            secondary: {
                main: '#0CBFF2',
                contrastText: '#e6e5e8',
            },
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: BLUE_THEME,
        palette: {
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: GREEN_THEME,
        palette: {
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: PURPLE_THEME,
        palette: {
            secondary: {
                main: '#673ab7',
                light: '#9575cd',
                dark: '#512da8',
            },
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: INDIGO_THEME,
        palette: {
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: ORANGE_THEME,
        palette: {
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: RED_THEME,
        palette: {
            primary: {
                main: '#8e3774',
                light: '#d097bf',
                dark: '#ac516c',
            },
            secondary: {
                main: '#ff5c8e',
                light: '#ecc3d0',
                dark: '#ce4c74',
            },
            mode: 'dark',
        },
    },
    {
        ...baseTheme,
        name: BLACK_THEME,
        palette: {
            mode: 'dark',
        },
    },
];

export const BuildRawTheme = (
    config: {
        theme: THEME_LIST;
    } = { theme: L2D_THEME },
    override: Partial<ThemeOptions> = {},
    activeMode: CustomizerReducerActiveModeType = 'light',
): Theme => {
    let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

    const baseMode = {
        palette: {
            mode: activeMode,
            background: {
                default: activeMode === 'dark' ? '#20232a' : '#F6F7FA',
                dark: activeMode === 'dark' ? '#1c2025' : '#ffffff',
                paper: activeMode === 'dark' ? '#282C34' : '#ffffff',
            },
            text: {
                primary: activeMode === 'dark' ? '#e6e5e8' : '#0A1A53',
                secondary: activeMode === 'dark' ? '#adb0bb' : lighten('#0A1A53', 0.5),
                gris70: activeMode === 'dark' ? '#adb0bb' : 'rgba(10, 26, 83, 0.7)',
                gris50: activeMode === 'dark' ? '#adb0bb' : 'rgba(10, 26, 83, 0.5)',
                gris35: activeMode === 'dark' ? '#adb0bb' : 'rgba(10, 26, 83, 0.35)',
                gris10: activeMode === 'dark' ? '#adb0bb' : 'rgba(10, 26, 83, 0.1)',
                gris005: activeMode === 'dark' ? '#adb0bb' : 'rgba(10, 26, 83, 0.05)',
                cyan: activeMode === 'dark' ? '#adb0bb' : 'rgba(23, 123, 194, 1)',
                light: lighten('#0A1A53', 0.35),
            },
        },
    };
    if (!themeOptions) {
        console.warn(new Error(`The theme ${config.theme} is not valid`));
        [themeOptions] = themesOptions;
    }
    let themeDarkOveride = {};
    if (activeMode === 'dark') {
        themeDarkOveride = themesDarkOveride.find((theme) => theme.name === config.theme) || {};
    }

    const theme = createTheme(
        _.merge({}, baseTheme, baseMode, themeOptions, themeDarkOveride, override, {}),
    );
    return theme;
};

export const BuildTheme = (
    config: {
        theme: THEME_LIST;
    } = { theme: L2D_THEME },
    override: Partial<ThemeOptions> = {},
): Theme => {
    const activeMode: CustomizerReducerActiveModeType = useSelector(
        (state: FlexyLayoutState) => state.customizer.activeMode,
    );
    return BuildRawTheme(config, override, activeMode);
};

export {
    TopbarHeight,
    SidebarWidth,
    SidebarFoldedWidth,
    paddingContent,
    footerHeight,
    breadcrumbHeight,
    baseTheme,
    themesOptions,
};
